export type PeriodRange = {
  start?: string;
  end?: string;
};

export type PeriodRangePreset = {
  label: string;
  value: PeriodRange | (() => PeriodRange);
};

export type PeriodValue = {
  year: number;
  period: number;
};

export type PeriodType = 'month' | 'quarter';

const ISO_DATE_PATTERN = /^(\d{4})-(\d{2})-(\d{2})$/;

function isValidYear(year: number) {
  return Number.isInteger(year) && year >= 1 && year <= 9999;
}

function getLastDayOfMonth(year: number, month: number) {
  const date = new Date(0);
  date.setUTCHours(0, 0, 0, 0);
  date.setUTCFullYear(year, month, 0);
  return date.getUTCDate();
}

function assertValidPeriod(value: PeriodValue, type: PeriodType) {
  const maximumPeriod = type === 'quarter' ? 4 : 12;
  if (
    !isValidYear(value.year) ||
    !Number.isInteger(value.period) ||
    value.period < 1 ||
    value.period > maximumPeriod
  ) {
    throw new RangeError(`Invalid ${type} period.`);
  }
}

function normalizeToken(value: string) {
  return value.trim().toLocaleLowerCase().replaceAll('.', '');
}

export function getMonthNames(
  locales: string | string[] | undefined,
  format: 'short' | 'long'
) {
  const formatter = new Intl.DateTimeFormat(locales, { month: format });
  return Array.from({ length: 12 }, (_, month) =>
    formatter.format(new Date(2000, month, 1))
  );
}

function resolveMonthNumber(
  token: string,
  locales?: string | string[]
): number | undefined {
  const numericMonth = Number(token);
  if (
    Number.isInteger(numericMonth) &&
    numericMonth >= 1 &&
    numericMonth <= 12
  ) {
    return numericMonth;
  }

  const normalizedToken = normalizeToken(token);
  const monthNames = [
    ...getMonthNames(locales, 'short'),
    ...getMonthNames(locales, 'long'),
  ];
  const index = monthNames.findIndex((monthName) => {
    const normalizedName = normalizeToken(monthName);
    return (
      normalizedName === normalizedToken ||
      (normalizedToken.length >= 3 &&
        (normalizedName.startsWith(normalizedToken) ||
          normalizedToken.startsWith(normalizedName)))
    );
  });

  return index === -1 ? undefined : (index % 12) + 1;
}

export function parseIsoPeriod(value: string): PeriodValue | undefined {
  const match = ISO_DATE_PATTERN.exec(value);
  if (!match) {
    return undefined;
  }

  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  const lastDay =
    month >= 1 && month <= 12 ? getLastDayOfMonth(year, month) : 0;
  if (
    !isValidYear(year) ||
    month < 1 ||
    month > 12 ||
    day < 1 ||
    day > lastDay
  ) {
    return undefined;
  }

  return { year, period: month };
}

export function isIsoDate(value: string): boolean {
  return parseIsoPeriod(value) != null;
}

export function formValueToPeriodPickerValue(
  value: string | null | undefined
): string | undefined {
  if (value == null || value === '') {
    return undefined;
  }

  const dateOnly = value.slice(0, 10);
  return isIsoDate(dateOnly) ? dateOnly : undefined;
}

export function comparePeriods(a: PeriodValue, b: PeriodValue) {
  return a.year === b.year ? a.period - b.period : a.year - b.year;
}

export function getPeriodStartIso(
  value: PeriodValue,
  type: PeriodType
): string {
  assertValidPeriod(value, type);
  const month = type === 'quarter' ? (value.period - 1) * 3 + 1 : value.period;
  return `${String(value.year).padStart(4, '0')}-${String(month).padStart(
    2,
    '0'
  )}-01`;
}

export function getPeriodEndIso(value: PeriodValue, type: PeriodType): string {
  assertValidPeriod(value, type);
  const month = type === 'quarter' ? value.period * 3 : value.period;
  const lastDay = getLastDayOfMonth(value.year, month);
  return `${String(value.year).padStart(4, '0')}-${String(month).padStart(
    2,
    '0'
  )}-${String(lastDay).padStart(2, '0')}`;
}

export function monthToIsoStart(month: number, year: number) {
  return getPeriodStartIso({ period: month, year }, 'month');
}

export function monthToIsoEnd(month: number, year: number) {
  return getPeriodEndIso({ period: month, year }, 'month');
}

export function quarterToIsoStart(quarter: number, year: number) {
  return getPeriodStartIso({ period: quarter, year }, 'quarter');
}

export function quarterToIsoEnd(quarter: number, year: number) {
  return getPeriodEndIso({ period: quarter, year }, 'quarter');
}

export function isoToPeriod(
  value: string | undefined,
  type: PeriodType
): PeriodValue | undefined {
  if (!value) {
    return undefined;
  }

  const month = parseIsoPeriod(value);
  if (!month) {
    return undefined;
  }

  return type === 'quarter'
    ? { year: month.year, period: Math.ceil(month.period / 3) }
    : month;
}

export function formatQuarter(value: PeriodValue) {
  assertValidPeriod(value, 'quarter');
  return `Q${value.period} ${value.year}`;
}

export function parseQuarter(input: string): PeriodValue | undefined {
  const match =
    /^\s*q([1-4])[\s./-]*(\d{4})\s*$/i.exec(input) ??
    /^\s*(\d{4})[\s./-]*q([1-4])\s*$/i.exec(input);
  if (!match) {
    return undefined;
  }

  const yearFirst = match[1].length === 4;
  const year = Number(match[yearFirst ? 1 : 2]);
  const period = Number(match[yearFirst ? 2 : 1]);
  return isValidYear(year) ? { year, period } : undefined;
}

export function formatMonth(value: PeriodValue, locales?: string | string[]) {
  assertValidPeriod(value, 'month');
  const date = new Date(0);
  date.setHours(12, 0, 0, 0);
  date.setFullYear(value.year, value.period - 1, 1);
  return new Intl.DateTimeFormat(locales, {
    month: 'short',
    year: 'numeric',
  }).format(date);
}

export function parseMonth(
  input: string,
  locales?: string | string[]
): PeriodValue | undefined {
  const match =
    /^\s*(\d{4})[\s./-]+([\p{L}.]+|\d{1,2})\s*$/u.exec(input) ??
    /^\s*([\p{L}.]+|\d{1,2})[\s./-]+(\d{4})\s*$/u.exec(input);
  if (!match) {
    return undefined;
  }

  const yearFirst = match[1].length === 4 && /^\d+$/.test(match[1]);
  const year = Number(match[yearFirst ? 1 : 2]);
  const period = resolveMonthNumber(match[yearFirst ? 2 : 1], locales);
  return period && isValidYear(year) ? { year, period } : undefined;
}

export function getCurrentPeriod(type: PeriodType, now = new Date()) {
  const month = now.getMonth() + 1;
  return {
    year: now.getFullYear(),
    period: type === 'quarter' ? Math.ceil(month / 3) : month,
  };
}
